import React from 'react';
import { formatCurrency } from '../../utils/index.ts';

const signed = (n: number) => `${n >= 0 ? '+' : '−'}${formatCurrency(Math.abs(n))}`;

export interface CashflowBridgeRow {
    label: React.ReactNode;
    sub?: React.ReactNode;
    amount: number;
    kind: 'start' | 'change';
}

export interface CashflowBridgeTotal {
    label: React.ReactNode;
    sub?: React.ReactNode;
    amount: number;
}

interface CashflowBridgeProps {
    rows?: CashflowBridgeRow[];
    total?: CashflowBridgeTotal;
}

/**
 * CashflowBridge — the reconciliation bridge used in every module's insight narrative.
 *
 * Walks the client from a starting figure (e.g. their Dashboard cashflow) through itemised
 * changes to a modelled total, so the numbers always reconcile on screen.
 *
 * rows: [{ label, sub?, amount, kind }]
 *   kind 'start'  → absolute position (surplus/deficit colour, no +/-)
 *   kind 'change' → a delta (+/- with green/red)
 * total: { label, sub?, amount }  → the bold reconciled result. For an exact on-screen tie,
 *   pass total.amount = start + sum(changes).
 */
export function CashflowBridge({ rows = [], total }: CashflowBridgeProps) {
    const amtClass = (n: number, kind: CashflowBridgeRow['kind']) =>
        kind === 'change'
            ? (n >= 0 ? 'text-emerald-600' : 'text-rose-600')
            : (n >= 0 ? 'text-teal-700' : 'text-rose-600');
    return (
        <div className="mt-1 overflow-hidden rounded-xl border border-slate-200">
            {rows.map((r, i) => (
                <div key={i} className={`flex items-start justify-between gap-3 px-3 py-2 ${i > 0 ? 'border-t border-slate-100' : ''}`}>
                    <span className="text-[12.5px] leading-tight text-slate-700">
                        {r.label}
                        {r.sub && <span className="mt-0.5 block text-[10.5px] font-normal text-slate-400">{r.sub}</span>}
                    </span>
                    <span className={`whitespace-nowrap text-[12.5px] font-bold ${amtClass(r.amount, r.kind)}`}>
                        {r.kind === 'change' ? signed(r.amount) : formatCurrency(r.amount)}
                    </span>
                </div>
            ))}
            {total && (
                <div className="flex items-start justify-between gap-3 border-t border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="text-[12.5px] font-bold leading-tight text-slate-900">
                        {total.label}
                        {total.sub && <span className="mt-0.5 block text-[10.5px] font-normal text-slate-400">{total.sub}</span>}
                    </span>
                    <span className={`whitespace-nowrap text-[12.5px] font-extrabold ${total.amount >= 0 ? 'text-teal-700' : 'text-rose-600'}`}>
                        {formatCurrency(total.amount)}
                    </span>
                </div>
            )}
        </div>
    );
}

export default CashflowBridge;
